import { useMemo, useRef } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { hourValueText } from './chartText';
import { useChartTokens } from './chartTokens';
import { activeHourRange, formatDecimal, formatHour, formatInt, HourPoint, Measure, measureValue } from './statisticsUtils';

interface HourlyChartProps {
    /** 24 puntos, uno por hora, con todo lo filtrado sumado. */
    points: HourPoint[];
    measure: Measure;
    ariaLabel: string;
    /** Cómo leerlo, para el lector de pantalla (no se ve). */
    description?: string;
}

const CHART_HEIGHT = 340;
const MEASURE_NAME_ID: Record<Measure, string> = {
    attentions: 'ServicesStatisticsMeasureAttentions',
    wait: 'ServicesStatisticsMeasureWait',
    service: 'ServicesStatisticsMeasureService',
};

function tooltipHtml(intl: IntlShape, point: HourPoint, measure: Measure): string {
    const title = intl.formatMessage({ id: 'ServicesStatisticsHourTitle' }, { hour: formatHour(point.hour) });
    return `<div class="ss-tip"><div class="ss-tip__title">${title}</div><div>${hourValueText(intl, point, measure)}</div></div>`;
}

/**
 * Curva por hora del día con una sola línea (todo lo filtrado). El eje va de la primera a la última hora con atenciones;
 * las horas sin atenciones dentro de ese tramo quedan en blanco.
 * TODO(design-system): gráficos y mapa de calor. Novarium no define el estilo de los gráficos ni los colores de series.
 */
export default function HourlyChart({ points, measure, ariaLabel, description }: HourlyChartProps) {
    const intl = useIntl();
    const ref = useRef<HTMLDivElement>(null);
    const tokens = useChartTokens(ref);

    const shown = useMemo(() => {
        const range = activeHourRange(points);
        return range ? points.slice(range.from, range.to + 1) : points;
    }, [points]);

    const apexSeries = useMemo(() => [{
        name: intl.formatMessage({ id: MEASURE_NAME_ID[measure] }),
        data: shown.map(point => measureValue(point, measure)),
    }], [intl, shown, measure]);

    const options = useMemo<ApexOptions | null>(() => {
        if (!tokens) return null;
        return {
            chart: {
                type: 'line',
                height: CHART_HEIGHT,
                fontFamily: tokens.font,
                foreColor: tokens.text2,
                toolbar: { show: false },
                zoom: { enabled: false },
                animations: { enabled: false },
            },
            colors: [tokens.primary],
            stroke: { width: 2, curve: 'straight', lineCap: 'round' },
            markers: { size: 4, strokeWidth: 0 },
            dataLabels: { enabled: false },
            legend: { show: false },
            grid: { borderColor: tokens.divider, strokeDashArray: 0 },
            xaxis: {
                categories: shown.map(point => String(point.hour).padStart(2, '0')),
                axisBorder: { color: tokens.divider },
                axisTicks: { color: tokens.divider },
                tooltip: { enabled: false },
                labels: { rotate: 0, hideOverlappingLabels: true, style: { fontSize: '12px' } },
                title: {
                    text: intl.formatMessage({ id: 'ServicesStatisticsAxisHour' }),
                    style: { fontSize: '12px', fontWeight: 500, color: tokens.text2 },
                },
            },
            yaxis: {
                min: 0,
                forceNiceScale: true,
                title: {
                    text: intl.formatMessage({ id: measure === 'attentions' ? 'ServicesStatisticsAxisPatientsPerDay' : 'ServicesStatisticsAxisMinutes' }),
                    style: { fontSize: '12px', fontWeight: 500, color: tokens.text2 },
                },
                labels: {
                    style: { fontSize: '12px' },
                    formatter: (value: number) => (measure === 'attentions' ? formatDecimal(value) : `${formatInt(value)} min`),
                },
            },
            tooltip: {
                shared: true,
                intersect: false,
                custom: ({ dataPointIndex }: { dataPointIndex: number }) => tooltipHtml(intl, shown[dataPointIndex], measure),
            },
        };
    }, [intl, tokens, shown, measure]);

    return (
        <div ref={ref} className="ss-chart" role="img" aria-label={ariaLabel} aria-description={description}>
            {options && (
                <ReactApexChart key={measure} type="line" height={CHART_HEIGHT} series={apexSeries} options={options} />
            )}
        </div>
    );
}
